import { adminApiFetch } from '@/services/adminApiFetch';

/**
 * F162 — admin-plane cross-tenant support ticket management. Mirrors
 * `adminMetricsService.ts` exactly: interfaces preserve the backend's
 * snake_case field names verbatim, one object-literal export with typed
 * methods, `adminApiFetch` for every call.
 */

// ─── Window event ─────────────────────────────────────────────────────────

/**
 * Dispatched (via `window.dispatchEvent(new Event(ADMIN_SUPPORT_CHANGED_EVENT))`)
 * whenever `AdminSupportPage` mutates a ticket or message — the open-count
 * nav badge in `AdminLayout` listens for this to refresh without polling.
 * Mirrors the existing `auth:unauthorized` / `admin:unauthorized` decoupled
 * cross-component event convention (F013/F059).
 */
export const ADMIN_SUPPORT_CHANGED_EVENT = 'admin-support:changed';

// ─── Shared cap ──────────────────────────────────────────────────────────

/**
 * The single frontend mirror of the backend's 1..5000 bound on
 * `support_ticket_messages.body`. The composer's `CharCounter` and its zod
 * schema both read this constant so they cannot drift apart from the API.
 */
export const SUPPORT_MESSAGE_BODY_MAX = 5000;

// ─── Types ───────────────────────────────────────────────────────────────

export type AdminSupportTicketReason = 'question' | 'problem' | 'suggestion' | 'other';
export type AdminSupportTicketStatus = 'open' | 'in_analysis' | 'closed';
export type AdminSupportMessageAuthorType = 'tenant_user' | 'admin' | 'ai_agent';
export type AdminSupportMessageState = 'draft' | 'sent';

export interface AdminSupportTicket {
  id: number;
  tenant_id: number;
  tenant_business_name: string;
  created_by_user_id: number | null;
  reason: AdminSupportTicketReason;
  description: string;
  status: AdminSupportTicketStatus;
  created_at: string;
  status_changed_at: string | null;
  status_changed_by_admin_id: number | null;
  message_count: number;
  last_message_at: string | null;
}

export interface AdminSupportMessage {
  id: number;
  tenant_id: number;
  ticket_id: number;
  author_type: AdminSupportMessageAuthorType;
  author_user_id: number | null;
  author_admin_id: number | null;
  body: string;
  state: AdminSupportMessageState;
  sent_at: string | null;
  sent_by_admin_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface AdminSupportTicketListParams {
  status?: AdminSupportTicketStatus;
  reason?: AdminSupportTicketReason;
  tenant_id?: number;
  page?: number;
  page_size?: number;
}

export interface AdminSupportTicketListResponse {
  items: AdminSupportTicket[];
  total: number;
  page: number;
  page_size: number;
  open_count: number;
}

export interface AdminSupportOpenCountResponse {
  open_count: number;
}

export interface AdminSupportTicketDetailResponse {
  ticket: AdminSupportTicket;
  messages: AdminSupportMessage[];
}

export interface CreateAdminSupportMessageRequest {
  body: string;
  author_type?: 'admin' | 'ai_agent';
  send?: boolean;
}

// ─── Query builder ───────────────────────────────────────────────────────

function buildListQuery(params: AdminSupportTicketListParams): string {
  const usp = new URLSearchParams();
  if (params.status) usp.append('status', params.status);
  if (params.reason) usp.append('reason', params.reason);
  if (params.tenant_id !== undefined) usp.append('tenant_id', String(params.tenant_id));
  if (params.page !== undefined) usp.append('page', String(params.page));
  if (params.page_size !== undefined) usp.append('page_size', String(params.page_size));
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

// ─── Service object ──────────────────────────────────────────────────────

export const adminSupportService = {
  list(params: AdminSupportTicketListParams = {}): Promise<AdminSupportTicketListResponse> {
    return adminApiFetch<AdminSupportTicketListResponse>(
      `/support/tickets${buildListQuery(params)}`
    );
  },

  getOpenCount(): Promise<AdminSupportOpenCountResponse> {
    return adminApiFetch<AdminSupportOpenCountResponse>('/support/open-count');
  },

  get(id: number): Promise<AdminSupportTicketDetailResponse> {
    return adminApiFetch<AdminSupportTicketDetailResponse>(`/support/tickets/${id}`);
  },

  createMessage(
    ticketId: number,
    payload: CreateAdminSupportMessageRequest
  ): Promise<AdminSupportMessage> {
    return adminApiFetch<AdminSupportMessage>(`/support/tickets/${ticketId}/messages`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  updateMessage(messageId: number, body: string): Promise<AdminSupportMessage> {
    return adminApiFetch<AdminSupportMessage>(`/support/messages/${messageId}`, {
      method: 'PATCH',
      body: JSON.stringify({ body }),
    });
  },

  sendMessage(messageId: number): Promise<AdminSupportMessage> {
    return adminApiFetch<AdminSupportMessage>(`/support/messages/${messageId}/send`, {
      method: 'POST',
    });
  },

  updateStatus(ticketId: number, status: AdminSupportTicketStatus): Promise<AdminSupportTicket> {
    return adminApiFetch<AdminSupportTicket>(`/support/tickets/${ticketId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },
};
